import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Star, Check, Trophy, Gift } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// Import split auth components
import { 
  LoginForm, 
  SignupForm, 
  SocialAuthButtons, 
  AuthBenefits, 
  EmailLookupForm 
} from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ContributorAuthModal - Shows after review submission to encourage registration
 * Also used for general contributor login/registration
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onSuccess: (contributor) => void
 * - reviewId?: string - If provided, will link review to contributor after auth
 * - mode?: "post-review" | "login" - Determines UI messaging
 */
export default function ContributorAuthModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  reviewId = null,
  mode = "post-review"
}) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [existingContributor, setExistingContributor] = useState(null);
  
  // Auth flow state
  const [authMode, setAuthMode] = useState("options"); // "options", "email-login", "email-register"
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAuthMode("options");
      setEmail("");
      setError("");
    }
  }, [isOpen]);

  // Check if user is already logged in
  useEffect(() => {
    if (!isOpen) return;
    
    const checkExistingSession = async () => {
      try {
        // Try localStorage first (persistent Google auth), then sessionStorage
        let sessionToken = localStorage.getItem("contributor_session_token") || sessionStorage.getItem("contributor_session_token");
        let contributorId = localStorage.getItem("contributor_id") || sessionStorage.getItem("contributor_id");
        
        if (sessionToken && contributorId) {
          // Verify the session is still valid
          const response = await axios.get(`${API}/contributors/auth/me`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });
          setExistingContributor(response.data);
          
          // If already logged in and we have a reviewId, link it
          if (reviewId && response.data?.id) {
            await linkReviewAndClose(response.data);
          }
        } else {
          setExistingContributor(null);
        }
      } catch (err) {
        // Session invalid or expired - clear it from both storages
        sessionStorage.removeItem("contributor_session_token");
        localStorage.removeItem("contributor_session_token");
        setExistingContributor(null);
      } finally {
        setChecking(false);
      }
    };
    
    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reviewId]);

  const linkReviewAndClose = async (contributor) => {
    if (!reviewId || !contributor?.id) {
      onSuccess?.(contributor);
      onClose();
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/contributors/link-review?review_id=${reviewId}&contributor_id=${contributor.id}`
      );
      toast.success(response.data.message || "Review linked! +10 points");
      onSuccess?.(contributor);
      onClose();
    } catch (err) {
      // Review might already be linked
      toast.info("Review submitted!");
      onSuccess?.(contributor);
      onClose();
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/contributor/auth/callback';
    
    // Store reviewId in sessionStorage so we can link after callback
    if (reviewId) {
      sessionStorage.setItem('pending_review_id', reviewId);
    }
    sessionStorage.setItem('auth_return_url', window.location.pathname);
    
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // Check if email exists
      const response = await axios.get(`${API}/contributors/by-email/${encodeURIComponent(email.trim())}`);
      
      if (response.data) {
        // Email exists - show login form
        setAuthMode("email-login");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // Email doesn't exist - show register form
        setAuthMode("email-register");
      } else {
        setError(err.response?.data?.detail || "Failed to check email");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (password) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/contributors/login`, {
        email: email.trim(),
        password
      });
      
      if (response.data.success) {
        const contributor = response.data.contributor || { id: response.data.contributor_id, email: response.data.email };
        const contributorId = contributor.id || response.data.contributor_id;
        const contributorEmail = contributor.email || response.data.email;
        
        // Store in localStorage for persistent login
        localStorage.setItem("contributor_id", contributorId);
        localStorage.setItem("contributor_email", contributorEmail);
        localStorage.setItem("contributor_auth_provider", "email");
        localStorage.setItem("contributor_login_time", new Date().toISOString());
        
        // Also store in sessionStorage for backward compatibility
        sessionStorage.setItem("contributor_id", contributorId);
        sessionStorage.setItem("contributor_email", contributorEmail);
        
        toast.success("Logged in successfully!");
        await linkReviewAndClose(contributor);
      } else if (response.data.needs_password) {
        // User exists but no password - show register
        setAuthMode("email-register");
        setError("Please set a password for your account");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async ({ password, confirmPassword }) => {
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // Try to register or set password
      const response = await axios.post(`${API}/contributors/set-password`, {
        email: email.trim(),
        password,
        confirm_password: confirmPassword
      });
      
      if (response.data.success || response.data.contributor_id) {
        // Now login
        const loginRes = await axios.post(`${API}/contributors/login`, {
          email: email.trim(),
          password
        });
        
        if (loginRes.data.success) {
          const contributor = loginRes.data.contributor || { id: loginRes.data.contributor_id, email: loginRes.data.email };
          const contributorId = contributor.id || loginRes.data.contributor_id;
          const contributorEmail = contributor.email || loginRes.data.email;
          
          // Store in localStorage for persistent login
          localStorage.setItem("contributor_id", contributorId);
          localStorage.setItem("contributor_email", contributorEmail);
          localStorage.setItem("contributor_auth_provider", "email");
          localStorage.setItem("contributor_login_time", new Date().toISOString());
          
          // Also store in sessionStorage for backward compatibility
          sessionStorage.setItem("contributor_id", contributorId);
          sessionStorage.setItem("contributor_email", contributorEmail);
          
          toast.success("Account created successfully!");
          await linkReviewAndClose(contributor);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast.info("Your review was submitted! Sign up anytime to earn points.");
    onClose();
  };

  const handleBack = () => {
    setAuthMode("options");
    setError("");
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {checking ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full mx-auto" />
              <p className="text-gray-500 mt-4">Checking session...</p>
            </div>
          ) : existingContributor ? (
            // Already logged in - show linking confirmation
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Welcome back!</h2>
                <p className="text-gray-600 mt-2">
                  Logged in as <strong>{existingContributor.email}</strong>
                </p>
              </div>
              
              {reviewId && (
                <button
                  onClick={() => linkReviewAndClose(existingContributor)}
                  className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-dark transition-colors flex items-center justify-center gap-2"
                  data-testid="link-review-btn"
                >
                  <Trophy className="w-5 h-5" />
                  Claim Your Points
                </button>
              )}
              
              <button
                onClick={onClose}
                className="w-full mt-3 py-2 text-gray-600 hover:text-gray-900"
              >
                Close
              </button>
            </div>
          ) : (
            // Not logged in - show auth options
            <>
              {/* Header */}
              <div className="relative bg-gradient-to-br from-brand to-brand-dark p-6 text-white">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white/80 hover:text-white"
                  data-testid="close-auth-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    {mode === "post-review" ? (
                      <Star className="w-6 h-6" />
                    ) : (
                      <User className="w-6 h-6" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {mode === "post-review" ? "Review Submitted!" : "Join Strider"}
                    </h2>
                    <p className="text-white/80 text-sm">
                      {mode === "post-review" 
                        ? "Sign up to earn points" 
                        : "Track your contributions"}
                    </p>
                  </div>
                </div>
                
                {mode === "post-review" && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg">
                    <Gift className="w-5 h-5 text-amber-300" />
                    <span className="text-sm">
                      <strong>+10 points</strong> waiting for you!
                    </span>
                  </div>
                )}
              </div>

              {/* Auth Content */}
              <div className="p-6">
                {authMode === "options" && (
                  <>
                    <AuthBenefits />
                    <SocialAuthButtons onGoogleLogin={handleGoogleLogin} loading={loading} />
                    <EmailLookupForm 
                      email={email}
                      setEmail={setEmail}
                      onSubmit={handleEmailLookup}
                      loading={loading}
                    />
                  </>
                )}

                {authMode === "email-login" && (
                  <LoginForm 
                    email={email}
                    onSubmit={handleEmailLogin}
                    onBack={handleBack}
                    loading={loading}
                    error={error}
                  />
                )}

                {authMode === "email-register" && (
                  <SignupForm 
                    email={email}
                    onSubmit={handleEmailRegister}
                    onBack={handleBack}
                    loading={loading}
                    error={error}
                  />
                )}

                {/* Skip Button - only for post-review mode and options view */}
                {mode === "post-review" && authMode === "options" && (
                  <button
                    onClick={handleSkip}
                    className="w-full mt-3 py-2 text-gray-500 text-sm hover:text-gray-700"
                    data-testid="skip-auth-btn"
                  >
                    Skip for now
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
